import React from 'react';
import {Box, Card, CardHeader, Table, TableBody, TableCell, TableContainer, Typography, TableRow as MuiTableRow} from '@mui/material';
import TableRow from './TableRow';
import {RecordContextProvider, RecordType} from 'components/record/RecordContext';
import {DragDropContext, Droppable, Draggable, DropResult} from '@hello-pangea/dnd';
import {useListContext} from './ListContext';
import {useResourceContext} from 'components/record/ResourceContext';
import {useMutate} from 'hooks/useFetchData';
import {ColumnVisibilityMenu, ColumnVisibilityProvider, useColumnVisibility, visibleColumnElements} from './columnVisibility';

const tableContainerSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflowX: 'auto'
} as const;

const tableSx = {
  width: 'max-content',
  minWidth: '100%',
  tableLayout: 'auto',
  borderCollapse: 'separate',
  borderSpacing: 0
} as const;

const NoRecords = ({colSpan}: {colSpan: number}) => {
  return (
    <MuiTableRow>
      <TableCell colSpan={colSpan} align="center">
        No records found.
      </TableCell>
    </MuiTableRow>
  );
};

const ListView = <T extends RecordType>(props: ListViewProps) => {
  const {resource} = useResourceContext();
  const storageKey = resource || window.location.pathname;

  return (
    <ColumnVisibilityProvider columns={props.children} storageKey={storageKey}>
      <ListViewContent {...props} />
    </ColumnVisibilityProvider>
  );
};

const ListViewContent = <T extends RecordType>(props: ListViewProps) => {
  const {
    children,
    bulkOptions = false,
    title,
    headerAvatar,
    headerAction = false,
    columnVisibility = false,
    toolbar,
    listFilters = <React.Fragment></React.Fragment>,
    tableToolbar,
    tableHead,
    tablePagination
  } = props;

  const {list, setList, isOrderable} = useListContext();
  const {resource, params} = useResourceContext();
  const visibility = useColumnVisibility();
  const visibleChildren = visibility ? visibleColumnElements(children, visibility.columns, visibility.overrides) : children;
  const visibleCount = React.Children.count(visibleChildren) + (bulkOptions ? 1 : 0);

  const [mutate] = useMutate<{items: T[]}>('REORDER', resource);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !isOrderable) return;

    const items = Array.from(list);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem as T);

    if (isOrderable) {
      if (typeof mutate === 'function') {
        mutate({
          variables: {payload: items.map((item, index) => ({id: item.id, order: index})), ...params}
        });
      }
      setList(items as T[]);
    }
  };

  return (
    <Card sx={{p: {xs: 1, sm: 2}, mb: {xs: 1, sm: 2}, position: 'relative', width: '100%', minWidth: 0, overflow: 'hidden'}}>
      <CardHeader
        avatar={headerAvatar}
        title={
          title && (
            <Typography component="div" variant="h5">
              {title}
            </Typography>
          )
        }
        action={
          <Box display="flex" alignItems="center">
            {columnVisibility && <ColumnVisibilityMenu />}
            {headerAction}
          </Box>
        }
        sx={{
          px: {xs: 0.5, sm: 2},
          py: {xs: 1, sm: 2},
          '& .MuiCardHeader-content': {minWidth: 0}
        }}
      />
      {toolbar && (
        <Box
          display="flex"
          alignItems="center"
          gap={{xs: 1, sm: 2}}
          flexWrap="wrap"
          sx={{width: '100%', px: {xs: 0.5, sm: 0}, mb: {xs: 1, sm: 0}}}
        >
          {toolbar}
        </Box>
      )}
      {listFilters}
      {tableToolbar}
      {isOrderable && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <TableContainer sx={tableContainerSx}>
            <Table sx={tableSx} size="small">
              {tableHead}
              <Droppable droppableId="table-body">
                {(provided) => (
                  <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                    {list.map((row, index) => (
                      <RecordContextProvider key={row.id} value={row}>
                        <Draggable draggableId={row.id.toString()} index={index}>
                          {(provided, snapshot) => {
                            return (
                              <TableRow
                                bulkOptions={bulkOptions}
                                id={row.id}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{
                                  backgroundColor: snapshot.isDragging ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                                  transition: 'background-color 0.2s ease'
                                }}
                              >
                                {visibleChildren}
                              </TableRow>
                            );
                          }}
                        </Draggable>
                      </RecordContextProvider>
                    ))}
                    {provided.placeholder}
                    {list.length === 0 && <NoRecords colSpan={visibleCount} />}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </TableContainer>
        </DragDropContext>
      )}
      {!isOrderable && (
        <TableContainer sx={tableContainerSx}>
          <Table sx={tableSx} size="small">
            {tableHead}
            <TableBody>
              {list.map((row) => (
                <RecordContextProvider key={row.id} value={row}>
                  <TableRow bulkOptions={bulkOptions} id={row.id}>
                    {visibleChildren}
                  </TableRow>
                </RecordContextProvider>
              ))}
              {list.length === 0 && <NoRecords colSpan={visibleCount} />}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {tablePagination}
    </Card>
  );
};

export type ListViewProps = {
  count?: number;
  children: JSX.Element | JSX.Element[];
  bulkOptions?: boolean;
  title?: React.ReactNode;
  headerAvatar?: React.ReactNode;
  headerAction?: JSX.Element & React.ReactNode;
  columnVisibility?: boolean;
  toolbar?: React.ReactNode;
  listFilters?: React.ReactNode;
  tableToolbar?: React.ReactNode;
  tableHead?: React.ReactNode;
  tablePagination?: React.ReactNode;
};

export default ListView;
